import React, { useState, CSSProperties } from 'react';
import { styled, css, Icon, Modal, Flex, Text } from '@goodrx/matisse';
import Webcam from 'react-webcam';
import { onCameraPermissionsAllowed, onCameraPermissionsError, trackEvent } from 'app/lib/analytics';
import Alert from 'app/lib/alert';
import { cropPhoto, fileToDataUrl } from 'app/routes/visit/tasks/utils/photoUtils';

const ImageTitle = styled(Text)`
  align-items: center;
  background: ${(p) => p.theme.colors.greyDarkest};
  border-radius: ${(p) => p.theme.space[2]}px;
  color: ${(p) => p.theme.colors.white};
  display: flex;
  font-weight: 600;
  height: 48px;
  justify-content: center;
  text-align: center;
  padding: ${(p) => p.theme.space[3]}px;
  z-index: 1;
`;

const IconButton = styled(Icon)`
  ${({ theme }) => css`
    height: 60px;
    width: 60px;
    line-height: 60px;
    font-size: 30px;
    background: white;
    border-radius: 100%;
    text-align: center;
    cursor: pointer;
    transition: background 250ms;
    box-shadow: ${theme.shadow.hard};

    &:hover {
      background: ${theme.colors.greyLighter};
    }
  `}
`;

const ModalStyled = styled(Modal)`
  overflow: hidden;
`;

const sharedWebcamImageStyles = {
  position: 'absolute',
  borderRadius: 8,
  width: 'auto',
  height: '100%',
};

interface WebcamModal {
  onSelect: (photoData: Blob) => void;
  onRequestClose: () => void;
  open: boolean;
  mirrored?: boolean;
  imageTitle?: string;
}

export const WebcamModal = ({ onSelect, onRequestClose, open, mirrored = false, imageTitle }: WebcamModal) => {
  const webcamRef = React.createRef<Webcam & HTMLVideoElement>();

  const [imageSrc, setImageSrc] = useState<string>();
  const [imageBlob, setImageBlob] = useState<Blob>();

  const capture = React.useCallback(async () => {
    const screenshot = webcamRef?.current?.getScreenshot();

    if (screenshot) {
      trackEvent('Photo captured');
      const file = await cropPhoto(screenshot);

      setImageBlob(file);
      setImageSrc(await fileToDataUrl(file));
    } else {
      Alert.error('Can not capture screenshot');
    }
  }, [webcamRef]);

  const clearImage = () => {
    setImageSrc(undefined);
  };

  const retake = () => {
    trackEvent('Photo retake');
    clearImage();
  };

  const use = () => {
    if (imageBlob) {
      trackEvent('Photo confirmed');
      onSelect(imageBlob);
    }

    handleClose();
  };

  const handleClose = () => {
    onRequestClose();
    clearImage();
  };

  const showWebcam = imageSrc || !open;

  return (
    <ModalStyled open={open} maxWidth={750} onClose={handleClose}>
      {/* 1:1 Aspect Ratio */}
      <Flex
        justifyContent="center"
        width={{ _: '50vw', xl: '25vw', md: '40vw', sm: '80vw' }}
        height={{ _: '50vw', xl: '25vw', md: '40vw', sm: '80vw' }}>
        <Icon
          name="close"
          color="white"
          onClick={handleClose}
          size={24}
          style={{ position: 'absolute', top: -32, right: 0, zIndex: 1 }}
        />

        {imageTitle && (
          <Flex width="100%" padding={2}>
            <ImageTitle>{imageTitle}</ImageTitle>
          </Flex>
        )}

        {showWebcam ? (
          <img src={imageSrc || undefined} alt="" style={sharedWebcamImageStyles as CSSProperties} />
        ) : (
          <Webcam
            ref={webcamRef}
            onUserMedia={onCameraPermissionsAllowed}
            onUserMediaError={(message) => onCameraPermissionsError({ message })}
            audio={false}
            mirrored={mirrored}
            style={sharedWebcamImageStyles as CSSProperties}
            videoConstraints={{ aspectRatio: 1 }}
            screenshotFormat="image/webp"
          />
        )}

        <Flex justifyContent="center" style={{ position: 'absolute', bottom: 16, width: '100%' }}>
          {imageSrc ? (
            <>
              <IconButton
                onClick={retake}
                name="close"
                color="red"
                style={{ marginRight: 8 }}
                data-testid="Reject Photo"
              />
              <IconButton
                onClick={use}
                name="check"
                color="secondary"
                style={{ marginLeft: 8 }}
                data-testid="Accept Photo"
              />
            </>
          ) : (
            <IconButton onClick={capture} name="camera" color="greyDarkest" data-testid="Take Photo" />
          )}
        </Flex>
      </Flex>
    </ModalStyled>
  );
};
